/**
 * MonthlyStats.tsx — 月間貸出返却予定（月間 貸出/返却 件数表） 2026-08-04
 *
 * シフト作成の参照用に、1ヶ月分の daylist を1画面に集約する。
 *   縦軸 = 日付（その月の全日）/ 横軸 = 2時間区切りの時間帯
 *   貸出（start_date 基準）と返却（end_date 基準）を別々の表で表示する
 *
 * 集計対象は daylist の既定表示ステータス（DEFAULT_VISIBLE_STATUS_CODES）と同一。
 * キャンセル済み・予約に至らなかった等は含めないので daylist の件数と整合する。
 *
 * データは ReservationDataContext のキャッシュ（約8ヶ月前〜3年後）から算出するため
 * 追加のAPI呼び出しは不要。月移動しても即時に再集計される。
 *
 * 仕様書: md/master_md/page-monthly-stats.md
 */
import React, { useCallback, useMemo, useState } from 'react';
import {
  Box, Typography, Paper, IconButton, Button, Table, TableBody, TableCell,
  TableHead, TableRow, CircularProgress, Tooltip,
} from '@mui/material';
import TableChartIcon from '@mui/icons-material/TableChart';
import DownloadIcon from '@mui/icons-material/Download';
import dayjs from 'dayjs';
import DashboardSidebar from '../components/DashboardSidebar';
import { useReservationData } from '../context/ReservationDataContext';
import { useStatusMaster } from '../hooks/useStatusMaster';
import { DEFAULT_VISIBLE_STATUS_CODES } from '../constants/status';
import { UI } from '../styles/uiTokens';

// 点検・車検の整備予約と米軍関連の予約は、通常の貸出/返却オペレーションではないため
// 集計から除外する（2026-08-10）。実データ上、これらは「予約名（customer_name）」に入っている
//   例: 「車検」「点検⭐️」「27th米軍車検」「27th米軍」「米軍台車」
// メモ(short_memo)側にも語が出るが、そちらは通常の貸出予約に付いた覚書のことがあるため見ない。
const EXCLUDED_NAME_KEYWORDS = ['点検', '車検', '米軍'];
const isExcludedByName = (r: any): boolean => {
  const name = String(r?.customer_name || '');
  return EXCLUDED_NAME_KEYWORDS.some(kw => name.includes(kw));
};

// 8時起点の2時間区切り。早朝（8時前）と夜間（20時以降）は端にまとめる
const BUCKETS = [
  { label: '〜8:00', from: 0, to: 8 },
  { label: '8:00', from: 8, to: 10 },
  { label: '10:00', from: 10, to: 12 },
  { label: '12:00', from: 12, to: 14 },
  { label: '14:00', from: 14, to: 16 },
  { label: '16:00', from: 16, to: 18 },
  { label: '18:00', from: 18, to: 20 },
  { label: '20:00〜', from: 20, to: 24 },
];

/** 時刻(0-23) → バケット index */
const bucketIndexOf = (hour: number): number => {
  if (hour < 8) return 0;
  if (hour >= 20) return BUCKETS.length - 1;
  return Math.floor((hour - 8) / 2) + 1;
};

const WEEKDAY_JP = ['日', '月', '火', '水', '木', '金', '土'];

interface DayRow {
  date: string;      // YYYY-MM-DD
  day: number;       // 日
  weekday: number;   // 0=日
  counts: number[];  // バケットごとの件数
  total: number;
}

const MonthlyStats: React.FC = () => {
  const [month, setMonth] = useState(() => dayjs().startOf('month'));
  const { reservations, isLoadingReservations } = useReservationData() as any;
  const { reservationStatusList } = useStatusMaster();

  // 集計対象ステータスの id セット（code ベースで解決。店舗ごとの採番差異に耐える）
  const allowedStatusIds = useMemo(() => {
    const set = new Set<number>();
    (reservationStatusList || []).forEach((s: any) => {
      if (s?.id != null && DEFAULT_VISIBLE_STATUS_CODES.includes(s.code)) set.add(Number(s.id));
    });
    return set;
  }, [reservationStatusList]);

  const isAllowedStatus = useCallback((r: any): boolean => {
    const s = r?.status;
    // status がコード文字列で入っている場合にも対応
    if (typeof s === 'string' && isNaN(Number(s))) return DEFAULT_VISIBLE_STATUS_CODES.includes(s);
    return allowedStatusIds.has(Number(s));
  }, [allowedStatusIds]);

  // ---- 月間集計（貸出 = start_date / 返却 = end_date）----
  const { departureRows, returnRows, ready } = useMemo(() => {
    const daysInMonth = month.daysInMonth();
    const ym = month.format('YYYY-MM');
    const mkRows = (): DayRow[] => Array.from({ length: daysInMonth }, (_, i) => {
      const d = month.add(i, 'day');
      return {
        date: d.format('YYYY-MM-DD'),
        day: i + 1,
        weekday: d.day(),
        counts: new Array(BUCKETS.length).fill(0),
        total: 0,
      };
    });
    const dep = mkRows();
    const ret = mkRows();

    // ステータスマスタ未取得の間は 0 件表示にならないよう ready=false で待つ
    const isReady = allowedStatusIds.size > 0 && Array.isArray(reservations);
    if (!isReady) return { departureRows: dep, returnRows: ret, ready: false };

    const tally = (rows: DayRow[], value: unknown) => {
      if (!value) return;
      const t = dayjs(String(value));
      if (!t.isValid() || t.format('YYYY-MM') !== ym) return;
      const row = rows[t.date() - 1];
      if (!row) return;
      const idx = bucketIndexOf(t.hour());
      row.counts[idx] += 1;
      row.total += 1;
    };

    reservations.forEach((r: any) => {
      if (!isAllowedStatus(r)) return;
      if (isExcludedByName(r)) return; // 点検・車検・米軍は除外
      tally(dep, r.start_date);
      tally(ret, r.end_date);
    });

    return { departureRows: dep, returnRows: ret, ready: true };
  }, [reservations, month, allowedStatusIds, isAllowedStatus]);

  const columnTotals = useCallback((rows: DayRow[]) => {
    const totals = new Array(BUCKETS.length).fill(0);
    let grand = 0;
    rows.forEach(r => {
      r.counts.forEach((c, i) => { totals[i] += c; });
      grand += r.total;
    });
    return { totals, grand };
  }, []);

  // ---- CSV ダウンロード（貸出・返却を1ファイルに2ブロックで出力）----
  const handleDownloadCsv = useCallback(() => {
    const header = ['date', ...BUCKETS.map(b => b.label), '合計'].join(',');
    const block = (title: string, rows: DayRow[]) => {
      const { totals, grand } = columnTotals(rows);
      const lines = rows.map(r =>
        [`${month.month() + 1}/${r.day}(${WEEKDAY_JP[r.weekday]})`, ...r.counts, r.total].join(',')
      );
      return [title, header, ...lines, ['合計', ...totals, grand].join(',')].join('\n');
    };
    const csv = [
      `${month.format('YYYY年M月')} 貸出・返却件数（${DEFAULT_VISIBLE_STATUS_CODES.length}ステータス集計 / 点検・車検・米軍は除外）`,
      '',
      block('貸出', departureRows),
      '',
      block('返却', returnRows),
      '',
    ].join('\n');

    // Excel で文字化けしないよう UTF-8 BOM を付ける
    const blob = new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `daylist_${month.format('YYYY-MM')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [month, departureRows, returnRows, columnTotals]);

  const renderTable = (title: string, rows: DayRow[], accent: string) => {
    const { totals, grand } = columnTotals(rows);
    const todayStr = dayjs().format('YYYY-MM-DD');
    return (
      <Paper variant="outlined" sx={{ mb: 3, overflow: 'hidden' }}>
        <Box sx={{ px: 1.5, py: 0.75, bgcolor: accent, color: '#fff' }}>
          <Typography sx={{ fontSize: 14, fontWeight: 700 }}>{title}（{grand}件）</Typography>
        </Box>
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ '& td, & th': { px: 1, py: 0.4, fontSize: 12, textAlign: 'center', whiteSpace: 'nowrap' } }}>
            <TableHead>
              <TableRow sx={{ bgcolor: UI.color.bgSubtle }}>
                <TableCell sx={{ fontWeight: 700, position: 'sticky', left: 0, bgcolor: UI.color.bgSubtle, zIndex: 1 }}>日付</TableCell>
                {BUCKETS.map(b => (
                  <TableCell key={b.label} sx={{ fontWeight: 700 }}>{b.label}</TableCell>
                ))}
                <TableCell sx={{ fontWeight: 700 }}>合計</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map(r => {
                const isToday = r.date === todayStr;
                const dayColor = r.weekday === 0 ? '#dc2626' : r.weekday === 6 ? '#2563eb' : UI.color.text;
                return (
                  <TableRow key={r.date} sx={{ bgcolor: isToday ? '#fff7ed' : (r.weekday === 0 || r.weekday === 6) ? '#fafafa' : 'inherit' }}>
                    <TableCell sx={{
                      fontWeight: isToday ? 800 : 600, color: dayColor,
                      position: 'sticky', left: 0, zIndex: 1,
                      bgcolor: isToday ? '#fff7ed' : (r.weekday === 0 || r.weekday === 6) ? '#fafafa' : '#fff',
                    }}>
                      {month.month() + 1}/{r.day}({WEEKDAY_JP[r.weekday]})
                    </TableCell>
                    {r.counts.map((c, i) => (
                      <TableCell key={i} sx={{
                        color: c === 0 ? '#cbd5e1' : UI.color.text,
                        fontWeight: c >= 5 ? 800 : 400,
                        bgcolor: c >= 5 ? '#fee2e2' : c >= 3 ? '#fef9c3' : 'inherit',
                      }}>{c}</TableCell>
                    ))}
                    <TableCell sx={{ fontWeight: 700, bgcolor: UI.color.bgSubtle }}>{r.total}</TableCell>
                  </TableRow>
                );
              })}
              <TableRow sx={{ bgcolor: UI.color.bgSubtle }}>
                <TableCell sx={{ fontWeight: 800, position: 'sticky', left: 0, bgcolor: UI.color.bgSubtle, zIndex: 1 }}>合計</TableCell>
                {totals.map((t, i) => <TableCell key={i} sx={{ fontWeight: 800 }}>{t}</TableCell>)}
                <TableCell sx={{ fontWeight: 800 }}>{grand}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Box>
      </Paper>
    );
  };

  const loading = isLoadingReservations || !ready;

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', background: '#f8fafc' }}>
      <DashboardSidebar />
      <Box sx={{ flexGrow: 1, p: 2, minWidth: 0 }}>
        {/* ヘッダー: タイトル + 月送り + CSV */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          <TableChartIcon sx={{ color: '#0f766e' }} />
          <Typography variant="h6" sx={{ fontWeight: 700, mr: 1 }}>月間貸出返却予定</Typography>

          <IconButton size="small" onClick={() => setMonth(m => m.subtract(1, 'month'))} sx={{ bgcolor: 'grey.100' }}>
            <Typography sx={{ fontSize: 16, fontWeight: 700 }}>&lt;</Typography>
          </IconButton>
          <Typography sx={{ fontSize: 16, fontWeight: 700, minWidth: 110, textAlign: 'center' }}>
            {month.format('YYYY年M月')}
          </Typography>
          <IconButton size="small" onClick={() => setMonth(m => m.add(1, 'month'))} sx={{ bgcolor: 'grey.100' }}>
            <Typography sx={{ fontSize: 16, fontWeight: 700 }}>&gt;</Typography>
          </IconButton>
          <Button size="small" variant="outlined" onClick={() => setMonth(dayjs().startOf('month'))}>今月</Button>

          <Box sx={{ flexGrow: 1 }} />
          <Tooltip title="表示中の月の貸出・返却件数をCSVでダウンロード">
            <span>
              <Button
                size="small" variant="contained" startIcon={<DownloadIcon />}
                onClick={handleDownloadCsv} disabled={loading}
              >CSV</Button>
            </span>
          </Tooltip>
        </Box>

        <Typography sx={{ fontSize: 11, color: UI.color.textSub, mb: 1.5 }}>
          シフト作成用の参照表。集計対象は貸出返却リストの既定表示ステータス
          （予約確定 / 貸出済み / 利用中 / オファー中 / 要連絡・未確定 / 保留中 / リクエスト）。
          キャンセル済み、および予約名に「点検」「車検」「米軍」を含む予約は含みません。
          時間帯は8時起点の2時間区切りです。
        </Typography>

        {loading ? (
          <Box sx={{ py: 8, textAlign: 'center' }}>
            <CircularProgress size={28} />
            <Typography sx={{ mt: 1, color: UI.color.textSub, fontSize: 13 }}>予約データを集計中…</Typography>
          </Box>
        ) : (
          <>
            {renderTable('貸出', departureRows, '#1d4ed8')}
            {renderTable('返却', returnRows, '#7b1fa2')}
          </>
        )}
      </Box>
    </Box>
  );
};

export default MonthlyStats;
